import type { VersionRestoreResult } from '@ralph/contracts'
import clsx from 'clsx'
import { ChevronDown, History, RotateCcw } from 'lucide-react'
import { useState } from 'react'
import { LintNotes } from '@/components/marketplace/maker/LintNotes.tsx'
import { Button, Spinner } from '@/components/ui/primitives.tsx'
import { useMakerVersions, useRestoreMakerVersion } from '@/hooks/use-maker.ts'
import { useI18n } from '@/i18n/index.ts'

/**
 * Version history in friendly clothing.
 *
 * The result of a restore is the interesting part and it has two halves the maker must see
 * together: what came back, and what could NOT come back. Restoring re-validates every
 * attachment against today's policy and catalogue, so an old version holding a capability
 * that has since been withdrawn returns without it — explicitly, with the list, never as a
 * silently reinstated grant (AGM-104).
 */
export const VersionsPanel = ({
  agentId,
  canEdit,
  onRestored
}: {
  agentId: string
  canEdit: boolean
  onRestored: () => void
}) => {
  // In the body, never at module scope: a captured catalogue branch keeps the language the
  // bundle evaluated in, exactly as a captured formatter keeps its locale (PRD-27 F-3).
  const { formatAgo, t } = useI18n()
  const strings = t.marketplace.maker.versions
  const [open, setOpen] = useState(false)
  const [result, setResult] = useState<VersionRestoreResult | null>(null)
  const versions = useMakerVersions(agentId, open)
  const restore = useRestoreMakerVersion(agentId)

  return (
    <div className="card rounded-panel px-3.5 py-3">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="flex w-full items-center gap-2.5 text-left font-sans text-[14px] font-semibold tracking-[-0.018em]"
      >
        <span
          aria-hidden
          className="flex h-[26px] w-[26px] flex-none items-center justify-center rounded-tile border border-rule bg-card-solid text-fg-muted"
        >
          <History size={13} strokeWidth={2} />
        </span>
        <span className="min-w-0 flex-1">{open ? strings.close : strings.open}</span>
        <ChevronDown
          size={13}
          strokeWidth={2.2}
          aria-hidden
          className={clsx('shrink-0 transition-transform duration-300', open && 'rotate-180')}
        />
      </button>

      {open && (
        <div className="mt-3">
          <p className="text-pretty text-[12.5px] leading-[1.5] text-fg-muted">{strings.lead}</p>

          {versions.isLoading && (
            <p className="readout mt-2.5 flex items-center gap-2 text-fg-subtle">
              <Spinner size={12} />
              {strings.loading}
            </p>
          )}
          {versions.data?.items.length === 0 && <p className="mt-2.5 text-[13px] text-fg-subtle">{strings.empty}</p>}

          <ul className="mt-2">
            {versions.data?.items.map((version) => (
              <li key={version.version} className="flex items-baseline gap-2 border-b border-rule py-2 last:border-0">
                <span className="min-w-0 flex-1 text-[13px] leading-snug">
                  {version.changeNote || <span className="text-fg-subtle">{strings.noNote}</span>}
                  <span className="readout mt-0.5 block text-fg-subtle">
                    {version.changedByName} · {formatAgo(version.createdAt)}
                  </span>
                </span>
                {canEdit && (
                  <Button
                    size="sm"
                    variant="mono"
                    icon={<RotateCcw size={11} strokeWidth={2.2} aria-hidden />}
                    disabled={restore.isPending}
                    onClick={() =>
                      restore.mutate(version.version, {
                        onSuccess: (restored) => {
                          setResult(restored)
                          onRestored()
                        }
                      })
                    }
                  >
                    {restore.isPending ? strings.restoring : strings.restore}
                  </Button>
                )}
              </li>
            ))}
          </ul>

          {restore.isError && <p className="mt-2.5 text-[12.5px] text-alert">{strings.failed}</p>}

          {result && (
            <div className="mt-3 rounded-tile border border-rule bg-card-solid px-3 py-2.5">
              <p className="text-[13px] font-semibold tracking-[-0.014em]">{strings.restoredTitle}</p>
              <p className="mt-1 text-pretty text-[12.5px] leading-[1.5] text-fg-muted">
                {strings.restoredBody(result.definitionVersion)}
              </p>

              {result.dropped.length > 0 && (
                <div className="mt-2.5">
                  <p className="text-[13px] font-semibold tracking-[-0.014em]">{strings.droppedTitle}</p>
                  <p className="mt-1 text-pretty text-[12.5px] leading-[1.5] text-fg-muted">{strings.droppedBody}</p>
                  <ul className="mt-1.5 list-disc space-y-1 pl-4 text-[12.5px]">
                    {result.dropped.map((entry) => (
                      <li key={`${entry.kind}:${entry.name}`}>{entry.name}</li>
                    ))}
                  </ul>
                </div>
              )}

              {result.lint.length > 0 && (
                <div className="mt-2.5">
                  <p className="text-[13px] font-semibold tracking-[-0.014em]">{strings.lintTitle}</p>
                  <LintNotes findings={result.lint} className="mt-1.5" />
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
